// Thin client for the coordinator's HTTP API and helpers to derive the
// signalling WebSocket URL and default ICE servers from the current origin.

import type { NodeInfo } from '@/engine/types';

/** A browser measurement session: an opaque token plus its lifetime. */
export interface Session {
  sessionId: string;
  token: string;
  expiresIn: number;
  /** Public client IP the coordinator saw (X-Forwarded-For aware). */
  clientIp?: string;
  /** Client ASN + organisation (0/"" when no GeoIP database is configured). */
  asn?: number;
  asnOrg?: string;
}

/** Everything the worker needs to open a signalling socket. */
export interface SignalingBootstrap {
  url: string;
  token: string;
  iceServers: RTCIceServer[];
  /** Coordinator-provided client identity (public IP + ASN) for this session. */
  sessionInfo?: { clientIp?: string; asn?: number; asnOrg?: string };
}

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: { accept: 'application/json' } });
  if (!res.ok) {
    throw new Error(`GET ${path} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

/** Fetch the current node list from the coordinator. */
export async function fetchNodes(): Promise<NodeInfo[]> {
  const nodes = await getJSON<NodeInfo[]>('/api/nodes');
  return Array.isArray(nodes) ? nodes : [];
}

/** Request a fresh signalling session token. */
export async function fetchSession(): Promise<Session> {
  return getJSON<Session>('/api/session');
}

/** WebSocket URL for the browser signalling endpoint on the current origin. */
export function signalingUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws`;
}

/**
 * Default ICE servers: the coordinator ships an embedded STUN server on UDP
 * 3478 of its host. Same-host / LAN runs also work with host candidates alone,
 * so an empty list is a valid fallback if STUN is disabled.
 */
export function defaultIceServers(): RTCIceServer[] {
  return [{ urls: `stun:${window.location.hostname}:3478` }];
}

/** Assemble the full signalling bootstrap for the worker. */
export async function bootstrapSignaling(): Promise<SignalingBootstrap> {
  const session = await fetchSession();
  return {
    url: signalingUrl(),
    token: session.token,
    iceServers: defaultIceServers(),
    sessionInfo: { clientIp: session.clientIp, asn: session.asn, asnOrg: session.asnOrg },
  };
}

/** Persist an opt-in result blob and return its share id. */
export async function saveResult(payload: unknown): Promise<string> {
  const res = await fetch('/api/results', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(`save result failed: ${res.status}`);
  }
  const body = (await res.json()) as { shareId: string };
  return body.shareId;
}

/** Fetch a previously stored result blob by share id. */
export async function fetchResult<T = unknown>(id: string): Promise<T> {
  const res = await fetch(`/api/results/${encodeURIComponent(id)}`, {
    headers: { accept: 'application/json' },
  });
  if (res.status === 404) {
    throw new Error('This shared result was not found or has expired.');
  }
  if (!res.ok) {
    throw new Error(`fetch result failed: ${res.status}`);
  }
  return (await res.json()) as T;
}
